import { safeJsonParse } from '../utilities/safeJsonParse';
import { baseProductConfigurationSlotId, baseProductConfigurationStepId } from '../utilities/strings';

import {
  fetchPersonalisationByProductId,
  fetchVariantsByProductId,
  fetchMetaobjectByMetafieldKey
} from './queries/fetchPersonalisationByProductId';
import { gatherRawPersonalisationData } from './gatherRawPersonalisationData';
import { componentGroupHasVariants } from '../utilities/componentGroupHasVariants';

/**
 * @typedef {Object} Step
 * @property {string} id - The ID of the step.
 * @property {string} handle - The handle of the step.
 * @property {number} minRequired - The minimum required number of items to select.
 * @property {number} orderIndex - The order index of the step.
 * @property {string} slotMode - The slot mode of the step.
 * @property {string[]} slots - The slots of the step.
 * @property {number|null} spacing - The spacing of the step.
 * @property {boolean} slotsAreOr - Whether the slots are OR.
 * @property {string} uiHeading - The UI heading of the step.
 * @property {string} [summaryLabel] - The summary label of the step.
 * @property {boolean} [showTabs] - Whether to show tabs for the step.
 * @property {string} [baseVisualiserImage] - The base visualiser image of the step.
 * @property {number} [maxSlotsSelectable] - The maximum number of slots that can be selected from the step.
 * @property {string} [stepType] - The type of the step.
 */

/**
 * @typedef {Object} Slot
 * @property {string} id - The ID of the slot.
 * @property {string} handle - The handle of the slot.
 * @property {string} slotType - The type of the slot.
 * @property {string} stepId - The ID of the step the slot belongs to.
 * @property {number} [columns] - The number of columns for the slot layout.
 * @property {string} [uiHeading] - The UI heading of the slot.
 * @property {string} [uiSubheading] - The UI subheading of the slot.
 * @property {string} [helpPage] - The help page for the slot.
 * @property {string[]} [allowedComponentGroups] - The allowed component groups for the slot.
 * @property {boolean} [isFreeSlot] - Whether the slot is free.
 * @property {number} [fee] - The fee for the slot.
 * @property {string} [feeDescription] - The fee description for the slot.
 * @property {boolean} [isRequired] - Whether the slot is required.
 * @property {number} [orderIndex] - The order index of the slot.
 * @property {number} [positionX] - The X position of the slot.
 * @property {number} [positionY] - The Y position of the slot.
 * @property {number} [scale] - The scale of the slot.
 * @property {string} [summaryLabel] - The summary label of the slot.
 * @property {boolean} [groupComponentsByProduct] - Whether to group components by product.
 * @property {string[]} [allowedFileTypes] - The allowed file types for the slot.
 * @property {number} [maxFileSize] - The maximum file size for the slot.
 * @property {string} [lineItemPropertyKey] - The line item property key for the slot.
 * @property {string} [translatedLineItemPropertyKey] - The line item property key for the group.
 * @property {string} [slotMode] - The slot mode from the step.
 * @property {number|null} [overridePrice] - The override price for the slot.
 * @property {boolean} [visualised] - Whether the slot is visualised.
 * @property {boolean} [fingerprint] - Whether the slot has fingerprint functionality.
 * @property {boolean} [engraving] - Whether the slot has engraving functionality.
 * @property {boolean} [doodle] - Whether the slot has doodle functionality.
 * @property {Object|null} [targetImagePlaceholder] - The target image placeholder for the slot.
 * @property {Object|null} [overlayImage] - An overlay image displayed on top of the mask shape.
 * @property {number} [rotation] - The rotation angle for the slot.
 * @property {boolean} [disabled] - Whether the slot is disabled.
 * @property {string[]} [uvInsertConfigHandles] - Allowed UV insert config handles for UV_INSERT slots.
 * @property {string} [inputLabel] - The label shown above the text input.
 * @property {string} [placeholderText] - The placeholder text for the input field.
 * @property {number} [maxLength] - Maximum character length for text inputs.
 * @property {string[]|string} [allowedFonts] - Fonts available for this slot (JSON string or array).
 * @property {string} [defaultFont] - The default font to pre-select for this slot.
 * @property {string} [allowedTextCase] - Enforced text case (e.g. 'uppercase').
 * @property {string} [groupName] - Shared group name for GROUPED steps (colour / style / foil option label).
 * @property {string} [textColour] - Hex colour for rendering text/engraving strokes (tints uploaded engraving images in the PhotoEditor and visualiser).
 * @property {string} [vasConfigId] - GID of the linked PersonalisationVasConfig metaobject.
 * @property {{ vasType: string, templateId: string, hasJig: boolean, jigId: string, placement: string, colour: string } | null} [vasConfig] - Resolved VAS config for order outputs.
 * @property {Object} [restrictions] - Input restriction rules (letters, numbers, symbols, spaces).
 * @property {import('../../../../../apps/gadget/web/utils/layoutBuilder/dataGateway/types').ExtendedVisualisationGroup} [visualisationGroup] - The visualisation group for the slot.
 * @property {import('../../../../../apps/gadget/web/shopify/metaobjects/types/personalisation/imageMaskZone').ImageMaskZone[]} [imageMaskZones] - The image mask zones for the slot.
 */

/**
 * @typedef {Object} AddonGroup
 * @property {string} id - The ID of the addon group.
 * @property {string} handle - The handle of the addon group.
 * @property {string} internalName - The internal name of the addon group.
 * @property {string[]} productVariantIds - The product variant IDs of the addon group.
 */

/**
 * @typedef {Object} AddonItem
 * @property {string} id - The ID of the addon item.
 * @property {string} handle - The handle of the addon item.
 * @property {string} internalName - The internal name of the addon item.
 * @property {string[]} productVariantIds - The product variant IDs of the addon item.
 */

/**
 * @typedef {Object} ComponentGroup
 * @property {number} columns - The number of columns in the component group.
 * @property {string|null} defaultSelectionLabel - The default selection label of the component group.
 * @property {string} handle - The handle of the component group.
 * @property {string} id - The ID of the component group.
 * @property {string} internalName - The internal name of the component group.
 * @property {string[]} productVariantIds - The product variant IDs of the component group.
 * @property {string|null} uiHeading - The UI heading of the component group.
 * @property {string|null} badge - The badge of the component group.
 * @property {string|null} helpPage - The help page for the component group.
 * @property {string} [priceModifierType] - The price modifier type of the component group.
 * @property {number} [priceModifierValue] - The price modifier value of the component group.
 * @property {boolean} [isTextGroup] - Whether the component group is a text group.
 */

/**
 * Fetches personalisation data for a product.
 * @param {Object} options - Options object.
 * @param {string} options.productId - The ID of the product to fetch personalisation data for.
 * @param {string} options.country - The country code of the user.
 * @returns {Promise<{personalisationDevModel: Object, stepsDevModel: Map, slotsDevModel: Map, addonGroupsDevModel: Map, addonItemsDevModel: Map, componentGroupsDevModel: Map, variantsDevModel: Map, hideSlotsOnVariantsSettings: Map}>} - An object containing the personalisation response, maps, and variants response.
 */
export const personalisationData = async ({ productId, country }) => {
  let personalisationDevModel = {};
  /** @type {Map<string, Step>} */
  const stepsDevModel = new Map();
  /** @type {Map<string, Slot>} */
  const slotsDevModel = new Map();
  /** @type {Map<string, AddonGroup>} */
  const addonGroupsDevModel = new Map();
  /** @type {Map<string, AddonItem>} */
  const addonItemsDevModel = new Map();
  /** @type {Map<string, ComponentGroup>} */
  const componentGroupsDevModel = new Map();
  const variantsDevModel = new Map();
  const hideSlotsOnVariantsSettings = new Map();

  const personalisationResponse = await fetchPersonalisationByProductId({
    productId,
    country
  });

  const {
    id,
    handle,
    internalName,
    personaliseCta,
    allowShareLink,
    slotMode,
    steps: stepsRaw,
    uiHeading,
    uiSubheading,
    helpPage,
    priceModifierType,
    priceModifierValue
  } = personalisationResponse;

  personalisationDevModel = {
    id,
    handle,
    internalName,
    personaliseCta,
    allowShareLink,
    slotMode,
    steps: safeJsonParse({ jsonString: stepsRaw }),
    uiHeading,
    uiSubheading,
    helpPage,
    priceModifierType,
    priceModifierValue
  };

  const [
    { personalisationSteps, personalisationSlots, addonGroups, addonItems, componentGroups, productVariants },
    { variants: baseProductVariants },
    baseProductConfigData
  ] = await Promise.all([
    gatherRawPersonalisationData({ personalisation: personalisationDevModel, country }),
    fetchVariantsByProductId({
      productId,
      country
    }),
    fetchMetaobjectByMetafieldKey({
      productId,
      metafieldKey: 'baseProductStepData'
    })
  ]);

  const baseSlotId = baseProductConfigurationSlotId;

  // Add the base step to the stepsDevModel first so it retains the order index of 0 - if the base product has more than one variant.
  if (baseProductVariants.length > 1) {
    // @ts-expect-error - this is to just set up the object.
    stepsDevModel.set(baseProductConfigurationStepId, {});
  }

  // End - Add initial config items for base product configuration step.

  // Set the object keys here so we can control them across the codebase.
  personalisationSteps
    .toSorted((a, b) => a.orderIndex - b.orderIndex)
    .forEach((step) => {
      const {
        id: stepId,
        handle: stepHandle,
        minRequired,
        orderIndex,
        // scale,
        slotMode,
        slots = [],
        slotsAreOr,
        spacing,
        uiHeading: stepUiHeading,
        showTabs,
        baseVisualiserImage,
        maxSlotsSelectable
      } = step;

      stepsDevModel.set(stepId, {
        id: stepId,
        handle: stepHandle,
        minRequired,
        orderIndex,
        slotMode,
        slots,
        slotsAreOr,
        spacing,
        uiHeading: stepUiHeading,
        showTabs: showTabs === true || showTabs === 'true',
        baseVisualiserImage: baseVisualiserImage?.image?.url,
        maxSlotsSelectable: maxSlotsSelectable || 0
      });
    });

  personalisationSlots.forEach((slot) => {
    const {
      allowedComponentGroups: slotAllowedComponentGroups,
      handle: slotHandle,
      id: slotId,
      isFreeSlot,
      isRequired,
      slotType,
      summaryLabel,
      uiHeading: slotUiHeading,
      uiSubheading: slotUiSubheading,
      helpPage: slotHelpPage,
      stepId,
      slotMode: slotModeFromStep,
      lineItemPropertyKey,
      translatedLineItemPropertyKey,
      groupComponentsByProduct,
      fee,
      hideOnVariantSelection,
      allowedFileTypes,
      visualised,
      orderIndex,
      // Destructure to exclude from spread.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      applyShadow: _applyShadow,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      positionX: _positionX,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      positionY: _positionY,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      scale: _scale,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      rotation: _rotation,
      fingerprint,
      engraving,
      doodle,
      targetImagePlaceholder,
      overlayImage,
      visualisationGroup,
      imageMaskZones,
      uvInsertConfigHandles
    } = slot;

    const hideOnVariantSelectionValues = safeJsonParse({ jsonString: hideOnVariantSelection || '[]' });
    // Create a map of variant ids that should hide slots when selected.
    hideOnVariantSelectionValues.forEach((variantId) => {
      hideSlotsOnVariantsSettings.set(variantId, [...(hideSlotsOnVariantsSettings.get(variantId) || []), slotId]);
    });

    const parsedUvInsertConfigHandles = safeJsonParse({ jsonString: uvInsertConfigHandles || '[]' });
    if (slotType === 'UV_INSERT') {
      console.log('[UV_INSERT slot]', slotId, 'uvInsertConfigHandles:', parsedUvInsertConfigHandles);
    }

    slotsDevModel.set(
      slotId,
      /** @type {Slot} */
      {
        ...slot,
        allowedFileTypes: safeJsonParse({ jsonString: allowedFileTypes || '[]' }),
        uvInsertConfigHandles: parsedUvInsertConfigHandles,
        allowedComponentGroups: safeJsonParse({ jsonString: slotAllowedComponentGroups || '[]' }),
        handle: slotHandle,
        id: slotId,
        isFreeSlot: isFreeSlot === true || isFreeSlot === 'true',
        isRequired: isRequired === true || isRequired === 'true',
        slotType,
        summaryLabel,
        uiHeading: slotUiHeading,
        uiSubheading: slotUiSubheading,
        helpPage: slotHelpPage,
        lineItemPropertyKey,
        translatedLineItemPropertyKey,
        stepId,
        slotMode: slotModeFromStep,
        groupComponentsByProduct: groupComponentsByProduct === true || groupComponentsByProduct === 'true',
        overridePrice: parseFloat(fee) || null,
        visualised: visualised === 'true',
        orderIndex,
        fingerprint: fingerprint === 'true',
        engraving: engraving === 'true',
        doodle: doodle === 'true',
        targetImagePlaceholder: targetImagePlaceholder?.image || null,
        overlayImage: overlayImage?.image || null,
        visualisationGroup: visualisationGroup || null,
        imageMaskZones: imageMaskZones || null
      }
    );
  });

  addonGroups.forEach((group) => {
    addonGroupsDevModel.set(group.id, group);
  });

  addonItems.forEach((item) => {
    addonItemsDevModel.set(item.id, item);
  });

  componentGroups.forEach((group) => {
    const {
      columns,
      defaultSelectionLabel,
      handle: groupHandle,
      id: groupId,
      internalName: groupInternalName,
      variants,
      uiHeading: groupUiHeading,
      badge,
      helpPage,
      priceModifierType: groupPriceModifierType,
      priceModifierValue: groupPriceModifierValue,
      isTextGroup: groupIsTextGroup
    } = group;

    componentGroupsDevModel.set(groupId, {
      columns,
      defaultSelectionLabel,
      handle: groupHandle,
      id: groupId,
      internalName: groupInternalName,
      productVariantIds: variants,
      uiHeading: groupUiHeading,
      badge,
      helpPage,
      priceModifierType: groupPriceModifierType,
      priceModifierValue: groupPriceModifierValue,
      isTextGroup: groupIsTextGroup === true || groupIsTextGroup === 'true'
    });
  });

  // Group variants by their first option (usually the product type)
  const secondaryOptionsMap = {};
  let uniqueProductVariants = Array.from(baseProductVariants.concat(productVariants));

  // @TODO: Find out why we are using the uniqueProductVariants concatenation here.
  // const baseAllowedComponentGroups = uniqueProductVariants.map(({ selectedOptions }) => selectedOptions?.[0]?.name);
  const baseAllowedComponentGroups = baseProductVariants.map(({ selectedOptions }) => selectedOptions?.[0]?.name);

  // Add secondary options name to each variant that has
  uniqueProductVariants = uniqueProductVariants
    .map((variant) => {
      const { selectedOptions } = variant;
      // Group names should always be the first option.
      const secondaryOptionsGroup = selectedOptions?.at(0);
      // Values for within the group should always be the second option.
      const secondaryOptions = selectedOptions?.at(1);

      // @TODO: Remove this when we are sure we don't need to disable secondary options for specific products.
      const disableSecondaryOptions = false;
      if (secondaryOptionsGroup && secondaryOptions) {
        secondaryOptionsMap[secondaryOptionsGroup?.value] = {
          ...(secondaryOptionsMap[secondaryOptionsGroup?.value] || {}),
          [secondaryOptions?.value]: variant
        };

        return {
          ...variant,
          secondaryOptionsName: secondaryOptionsGroup?.value,
          secondaryOptionsLabel: secondaryOptions?.name,
          secondaryOptionsGroupName: secondaryOptionsGroup?.value,
          disableSecondaryOptions
        };
      }

      return variant;
    })
    .map((variant) => {
      const {
        secondaryOptionsName: secondaryOptionsNameValue,
        secondaryOptionsLabel: secondaryOptionsLabelValue,
        secondaryOptionsGroupName,
        disableSecondaryOptions,
        id: variantId,
        compareAtPrice,
        image,
        price,
        shortDescription,
        templateId,
        title,
        availableForSale,
        groupId: componentGroupId,
        asciiSymbol,
        hideFromVisualiser,
        variantsCount
      } = variant;
      const theSecondaryOptions = Object.values(secondaryOptionsMap[`${secondaryOptionsGroupName}`] || {})?.map(
        (option) => ({
          ...option,
          // Remove the group name so we just have the option name.
          title: option.title.replace(`${secondaryOptionsGroupName} /`, '')
        })
      );

      const groupHero =
        theSecondaryOptions.length === 1 || theSecondaryOptions.findIndex((option) => option.id === variant.id) === 0;

      variantsDevModel.set(variantId, {
        product: variant.product,
        id: variantId,
        image,
        price,
        compareAtPrice,
        shortDescription,
        title,
        availableForSale,
        componentGroupId,
        secondaryOptionsName: secondaryOptionsNameValue,
        secondaryOptions: theSecondaryOptions,
        secondaryOptionsLabel: secondaryOptionsLabelValue,
        groupHero,
        disableSecondaryOptions,
        asciiSymbol: asciiSymbol?.value,
        hideFromVisualiser: hideFromVisualiser?.value === 'true',
        variantsCount: variantsCount?.count || 0,
        templateId: templateId?.value || null
      });

      return {
        ...variant,
        secondaryOptionsGroupName,
        secondaryOptionsName: secondaryOptionsNameValue,
        secondaryOptions: theSecondaryOptions,
        secondaryOptionsLabel: secondaryOptionsLabelValue,
        disableSecondaryOptions,
        groupHero
      };
    });

  // --- Begin: Add base product configuration step and slot ---
  /* Check if variant has more than one variant, else we don't need to set the base product configuration step and slot. */

  // @TODO: Check why we have additional keys in these objects vs organic objects.
  if (baseProductVariants.length > 1) {
    stepsDevModel.set(baseProductConfigurationStepId, {
      id: baseProductConfigurationStepId,
      handle: baseProductConfigurationStepId,
      minRequired: 1,
      // Place at the start of the flow
      orderIndex: 0,
      // slotMode: 'AND',
      slots: [baseSlotId],
      slotsAreOr: false,
      spacing: null,
      uiHeading: baseProductConfigData?.stepHeading || 'Select your base product',
      stepType: baseProductConfigurationStepId
    });

    const baseProductVariantsGroups = uniqueProductVariants.reduce(
      (acc, { id: baseProductVariantId, selectedOptions }) => {
        const groupName = selectedOptions?.[0]?.name;
        const existingGroup = acc.find((group) => group.groupName === groupName);
        if (!existingGroup) {
          acc.push({ groupName, variants: [baseProductVariantId] });
        }
        if (existingGroup) {
          existingGroup.variants.push(baseProductVariantId);
        }
        return acc;
      },
      []
    );

    baseProductVariantsGroups.forEach(({ groupName, variants }) => {
      componentGroupsDevModel.set(groupName, {
        columns: 2,
        defaultSelectionLabel: null,
        handle: groupName,
        id: groupName,
        internalName: groupName,
        productVariantIds: variants.map((variantId) => variantId),
        uiHeading: null,
        badge: null,
        helpPage: null
      });

      slotsDevModel.set(baseSlotId, {
        columns: 2,
        id: baseSlotId,
        handle: baseProductConfigurationSlotId,
        slotType: 'BASE_CONFIG',
        stepId: baseProductConfigurationStepId,
        uiHeading: baseProductConfigData?.uiHeading || null,
        uiSubheading: baseProductConfigData?.uiSubheading || null,
        helpPage: baseProductConfigData?.helpPage || null,
        allowedComponentGroups: [...new Set(baseAllowedComponentGroups)],
        isFreeSlot: false,
        isRequired: true,
        positionX: 0,
        positionY: 0,
        scale: 1,
        summaryLabel: baseProductConfigData?.summaryLabel || 'Base Product'
      });
    });
    // --- End: Add base product configuration step and slot ---
  }

  // Check that each slot has at least one variant.
  Array.from(slotsDevModel.entries()).forEach(([slotId, slot]) => {
    if (
      !componentGroupHasVariants({
        theSlotsAllowedComponentGroups: slot.allowedComponentGroups,
        componentGroups: componentGroupsDevModel,
        variants: variantsDevModel
      }) &&
      slot.slotType === 'COMPONENT'
    ) {
      slotsDevModel.delete(slotId);
    }
  });

  // Remove any steps that don't have any slots left.
  Array.from(stepsDevModel.entries()).forEach(([stepId, step]) => {
    if (step?.slots?.every((slotId) => !slotsDevModel.has(slotId))) {
      stepsDevModel.delete(stepId);
    }
  });

  return {
    personalisationDevModel,
    stepsDevModel,
    slotsDevModel,
    addonGroupsDevModel,
    addonItemsDevModel,
    componentGroupsDevModel,
    variantsDevModel,
    hideSlotsOnVariantsSettings
  };
};
